/**
 * Configuración → Monedas (`/configuracion/monedas`).
 *
 * Puerto de `Cat_currency_types` + `views/currency_types/index.php`. El panel
 * anterior **sólo permitía editar**: la vista no tenía botón «Nuevo» y
 * `store()` siempre llamaba a `update()`, así que el catálogo se sembraba por
 * base de datos. Se mantiene tal cual (informando en la pantalla) y se añade el
 * estado activo, que la vista mostraba como «Si/No».
 */

import { ArrowLeft, Coins, Info, Pencil, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';

import { useMonedas, useMutarMonedas } from '../catalogos.api';
import type { Moneda } from '../catalogos.types';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

const BASE_BANDERA =
  'inline-flex items-center rounded-control px-3 py-1.5 text-xs font-semibold transition-colors';
const BANDERA_ACTIVA = 'bg-ink-900 text-white';
const BANDERA_INACTIVA = 'border border-ink-200 bg-white text-ink-700 hover:border-brand-300';

/** Edición de una moneda (el código no se toca). */
function MonedaDialogo({
  moneda,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  moneda: Moneda;
  guardando: boolean;
  error: string | null;
  onGuardar: (payload: { descripcion: string; simbolo: string; activo: boolean }) => void;
  onCerrar: () => void;
}) {
  const [descripcion, setDescripcion] = useState(moneda.descripcion);
  const [simbolo, setSimbolo] = useState(moneda.simbolo);
  const [activo, setActivo] = useState(moneda.activo);

  const valido = descripcion.trim().length > 0;

  return (
    <Dialog
      open
      onClose={onCerrar}
      title={`Editar moneda ${moneda.codigo}`}
      description="El código ISO es la clave de la tabla: no se puede cambiar."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!valido}
            onClick={() => onGuardar({ descripcion: descripcion.trim(), simbolo: simbolo.trim(), activo })}
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error" title="No se pudo guardar">{error}</Alert> : null}

        <Input label="Código" value={moneda.codigo} disabled onChange={() => undefined} />

        <Input
          label="Descripción"
          value={descripcion}
          maxLength={255}
          autoFocus
          placeholder="Ej. Soles"
          onChange={(evento) => setDescripcion(evento.target.value)}
        />

        <Input
          label="Símbolo"
          value={simbolo}
          maxLength={8}
          placeholder="Ej. S/"
          onChange={(evento) => setSimbolo(evento.target.value)}
        />

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-ink-700">Estado</span>
          {[true, false].map((valor) => (
            <button
              key={String(valor)}
              type="button"
              aria-pressed={activo === valor}
              className={`${BASE_BANDERA} ${activo === valor ? BANDERA_ACTIVA : BANDERA_INACTIVA}`}
              onClick={() => setActivo(valor)}
            >
              {valor ? 'Activa' : 'Inactiva'}
            </button>
          ))}
        </div>
      </div>
    </Dialog>
  );
}

export function MonedasPage() {
  useDocumentTitle('Configuración · Monedas');

  const monedas = useMonedas();
  const mutaciones = useMutarMonedas();

  const [editando, setEditando] = useState<Moneda | null>(null);
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);
  const [errorDialogo, setErrorDialogo] = useState<string | null>(null);

  const sinPermiso = monedas.error instanceof ApiError && monedas.error.status === 403;
  const lista = monedas.data?.monedas ?? [];

  const cabecera = (
    <header>
      <Link
        to="/configuracion"
        className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
      >
        <ArrowLeft className="size-3" aria-hidden="true" />
        Configuración
      </Link>
      <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
        <Coins className="size-6 text-brand-500" aria-hidden="true" />
        Monedas
      </h1>
      <p className="mt-1 max-w-2xl text-sm text-ink-500">
        Monedas del sistema, con su símbolo y su estado. Las usan las cuentas bancarias y los
        comprobantes.
      </p>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>Configuración</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  const guardar = (payload: { descripcion: string; simbolo: string; activo: boolean }) => {
    if (!editando) return;
    setErrorDialogo(null);

    mutaciones.actualizar.mutate(
      { codigo: editando.codigo, payload },
      {
        onSuccess: (datos) => {
          setEditando(null);
          setAviso({ tono: 'success', texto: datos.mensaje });
        },
        onError: (error) => setErrorDialogo(mensajeDe(error, 'No se pudo guardar la moneda.')),
      },
    );
  };

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? (
        <Alert variant={aviso.tono === 'success' ? 'success' : 'error'}>{aviso.texto}</Alert>
      ) : null}

      <Alert variant="info" title="Catálogo cerrado">
        <span className="flex items-start gap-2">
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>
            Igual que en el panel anterior, aquí sólo se <strong>editan</strong> las monedas
            existentes: el código ISO (PEN, USD…) se define al crear la moneda en la base de datos.
          </span>
        </span>
      </Alert>

      {monedas.isLoading ? (
        <Skeleton className="h-56 w-full" />
      ) : monedas.error && !sinPermiso ? (
        <Alert variant="error" title="No se pudieron leer las monedas">
          {mensajeDe(monedas.error, 'La API no respondió.')}
        </Alert>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Listado de monedas</CardTitle>
            <Badge variant="neutral">{monedas.data?.total ?? 0} registradas</Badge>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[34rem] border-collapse text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                    <th className="w-24 pb-2">Código</th>
                    <th className="pb-2">Descripción</th>
                    <th className="w-24 pb-2">Símbolo</th>
                    <th className="w-28 pb-2">Estado</th>
                    <th className="w-24 pb-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((moneda) => (
                    <tr key={moneda.codigo} className="border-t border-ink-100">
                      <td className="py-3">
                        <span className="rounded-control bg-ink-100 px-2 py-0.5 font-mono text-xs font-semibold text-ink-700">
                          {moneda.codigo}
                        </span>
                      </td>
                      <td className="py-3 font-medium text-ink-900">{moneda.descripcion}</td>
                      <td className="py-3 text-ink-700">{moneda.simbolo || '—'}</td>
                      <td className="py-3">
                        <Badge variant={moneda.activo ? 'success' : 'neutral'}>
                          {moneda.activo ? 'Activa' : 'Inactiva'}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Editar ${moneda.descripcion}`}
                            title="Editar"
                            onClick={() => {
                              setErrorDialogo(null);
                              setEditando(moneda);
                            }}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {editando ? (
        <MonedaDialogo
          key={`moneda-${editando.codigo}`}
          moneda={editando}
          guardando={mutaciones.actualizar.isPending}
          error={errorDialogo}
          onGuardar={guardar}
          onCerrar={() => setEditando(null)}
        />
      ) : null}
    </div>
  );
}
